import { CBSocketClient, SocketClient } from "cbcore-ts"
import { IF, nil, NO, UIColor, UIRoute, UITextView, UITooltip, UIView, UIViewBroadcastEvent, UIViewController, YES } from "uicore-ts"
import { RHCore } from "../../Core/RHCore"
import { RHContractingAuthorityRules } from "../../ContractingAuthorityRules"
import { CBDialogViewShower } from "../../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHAlertBannerView } from "../../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHCalloutView } from "../../Custom components/RH/RHCalloutView"
import { RHCheckbox } from "../../Custom components/RH/RHCheckbox"
import { RHDataView } from "../../Custom components/RH/RHDataView"
import { RHDetailsDialogViewController } from "../../Custom components/RH/RHDetailsDialogViewController"
import { RHTextField } from "../../Custom components/RH/RHTextField"
import { RHValidationIssuesDialogView } from "../../Custom components/RH/RHValidationIssuesDialogView"
import { RHButtons, RHColors, RHFont, RHForms, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHTextSanitizer } from "../../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHCompanyAdministrationViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import {
    CompanySearchResult,
    RHCompanyAffiliation,
    RHCompanyMember,
    RHCompanyMembershipRequestRole,
    RHCompanyMembershipWorkflow,
    RHEmailDeliveryOutcome
} from "../../SocketClientFunctions"
import { CompanyAdministrationMembersView } from "../CompanyAdministration/CompanyAdministrationMembersView"
import { CompanyRoleBadgesView } from "../CompanyAdministration/CompanyRoleBadgesView"
import { CompanyAffiliationWorkflowsView } from "./CompanyAffiliationWorkflowsView"
import { CompanyContractingAuthoritySettingsView } from "./CompanyContractingAuthoritySettingsView"


type CompanyAffiliationNoActiveCompanyExplanation = {
    text: string
    tone: "info" | "warning"
}


type CompanySearchRow = CompanySearchResult & {
    registrationStatusText: string
}


class RequestCompanyMembershipDialogView extends UIView {

    static readonly companySelectionIdentifier = "requestCompanyMembershipCompany"
    static readonly _explanationText =
        "Otsi ettevõte. Olemasoleva ettevõttega seostamiseks vali roll; " +
        "uue ettevõtte saad esmalt registreerida."

    readonly titleLabel = new UITextView(nil, UITextView.type.paragraph)
        .configuredWithObject({
            text: "Seo ettevõte",
            textColor: RHColors.textHeading,
            style: {
                fontSize: RHFont.sizeLarge,
                fontWeight: RHFont.weightSemibold
            }
        })
        .addedAsSubviewToView(this)

    readonly explanationButton = new RHButton()
        .configuredWithObject({
            titleLabel: {
                text: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" ' +
                    'fill="none" aria-hidden="true" focusable="false">' +
                    '<circle cx="8" cy="8" r="6.25" stroke="currentColor" stroke-width="1.5"/>' +
                    '<path d="M8 7.25V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
                    '<circle cx="8" cy="4.75" r="0.85" fill="currentColor"/></svg>'
            },
            variant: "link",
            contentPadding: 0,
            hoverText: "",
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    CBDialogViewShower.alert(RequestCompanyMembershipDialogView._explanationText)
                }
            }
        })
        .addedAsSubviewToView(this)

    readonly searchTextField = new RHTextField()
        .configuredWithObject({
            placeholderText: "Ettevõtte nimi või registrikood"
        })
        .addedAsSubviewToView(this)

    readonly searchButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Otsi", style: { fontSize: RHButtons.compactFontSize } },
            variant: "primary",
            contentPadding: RHButtons.compactContentPadding
        })
        .addedAsSubviewToView(this)

    readonly selectedCompanyLabel = new UITextView()
        .configuredWithObject({
            text: "Ettevõte valimata",
            isSingleLine: YES,
            textColor: RHColors.textSecondary
        })
        .addedAsSubviewToView(this)

    readonly searchResultsDataView = new RHDataView<CompanySearchRow>()
        .configuredWithObject({
            isSearchAvailable: NO,
            noDataMessage: "Otsi ettevõtet ülalt",
            descriptors: [
                { title: "Ettevõtte nimi", keyPath: "name", allowSorting: NO },
                { title: "Registrikood", keyPath: "businessRegistryCode", allowSorting: NO },
                { title: "Staatus", keyPath: "registrationStatusText", allowSorting: NO },
                {
                    type: "selection",
                    identifier: RequestCompanyMembershipDialogView.companySelectionIdentifier,
                    selectionMode: "single",
                    masterSelectionEnabled: NO,
                    title: "Vali"
                }
            ],
            selectionDidChange: (identifier: string) => {
                const selection = this.searchResultsDataView.selectionForIdentifier(identifier)
                if (selection[0]) {
                    this._selectedCompany = selection[0].dataItem
                    this.selectedCompanyLabel.text =
                        `Valitud: ${this._selectedCompany.name} ` +
                        `(${this._selectedCompany.businessRegistryCode})`
                    this._hideError()
                    this._updateForCompanySelection()
                }
                else {
                    this._selectedCompany = undefined
                    this.selectedCompanyLabel.text = "Ettevõte valimata"
                    this._updateForCompanySelection()
                }
                this.setNeedsLayout()
            }
        })
        .addedAsSubviewToView(this)

    readonly rolesLabel = new UITextView()
        .configuredWithObject({
            text: "Soovitud roll",
            textColor: RHColors.textHeading,
            isSingleLine: YES,
            style: { fontWeight: RHFont.weightSemibold }
        })
        .addedAsSubviewToView(this)

    readonly administratorCheckbox = new RHCheckbox()
        .configuredWithObject({
            titleLabel: {
                text: RHCompanyAdministrationViewTokens.roleDisplayLabels.administrator
            }
        })
        .addedAsSubviewToView(this)

    readonly procurerCheckbox = new RHCheckbox()
        .configuredWithObject({
            titleLabel: {
                text: RHCompanyAdministrationViewTokens.roleDisplayLabels.procurer
            }
        })
        .addedAsSubviewToView(this)

    readonly procurerIneligibleLabel = new UITextView()
        .configuredWithObject({
            text: RHCompanyAdministrationViewTokens.procurerIneligibleExplanation,
            textColor: RHColors.warningAmberText,
            isSingleLine: NO,
            style: { fontSize: RHFont.sizeSmall }
        })

    readonly offererCheckbox = new RHCheckbox()
        .configuredWithObject({
            titleLabel: {
                text: RHCompanyAdministrationViewTokens.roleDisplayLabels.offerer
            }
        })
        .addedAsSubviewToView(this)

    readonly registrationInfoAlertView = new RHAlertBannerView()
        .configuredWithObject({
            alertType: RHAlertBannerView.type.Warning,
            text: RequestCompanyMembershipDialogView._registrationExplanation(),
            textAlignment: UITextView.textAlignment.center
        })

    readonly warningAlertView = new RHAlertBannerView()
        .configuredWithObject({
            alertType: RHAlertBannerView.type.Warning
        })

    readonly cancelButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Sulge", style: { fontSize: RHButtons.compactFontSize } },
            variant: RHButtons.destructiveUtilityVariant,
            contentPadding:
                RHCompanyAdministrationViewTokens.membershipRequestDialog.footerButtonContentPadding
        })
        .addedAsSubviewToView(this)

    readonly submitButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Taotle", style: { fontSize: RHButtons.compactFontSize } },
            variant: "success",
            contentPadding:
                RHCompanyAdministrationViewTokens.membershipRequestDialog.footerButtonContentPadding
        })
        .addedAsSubviewToView(this)

    override _focusManager = new RHFocusManager(() => {
        const items: UIView[] = [
            this.explanationButton,
            this.searchTextField,
            this.searchButton,
            this.searchResultsDataView._gridContainerView
        ]
        if (this.rolesLabel.superview === this) {
            items.push(
                this.administratorCheckbox,
                this.procurerCheckbox,
                this.offererCheckbox
            )
        }
        items.push(this.cancelButton, this.submitButton)
        return items
    })

    _selectedCompany?: CompanySearchResult
    _companySearchDelayTimer?: ReturnType<typeof setTimeout>
    _companySearchGeneration = 0


    constructor(elementID?: string) {
        super(elementID)
        this.configureWithObject({
            backgroundColor: RHCompanyAdministrationViewTokens.membershipRequestDialog.backgroundColor,
            style: {
                borderRadius: RHCompanyAdministrationViewTokens.membershipRequestDialog.borderRadius,
                boxShadow: RHCompanyAdministrationViewTokens.membershipRequestDialog.boxShadow
            }
        })
        this.explanationButton.viewHTMLElement.setAttribute("aria-label", "Ettevõtte sidumise juhis")
        UITooltip.sharedInstance.attach(this.explanationButton, RequestCompanyMembershipDialogView._explanationText)
        this.searchButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            await this._performSearch()
        }
        this.searchTextField.textElementView.controlEventTargetAccumulator.EnterDown = (_sender, event) => {
            this.searchButton.sendControlEventForKey(UIView.controlEvent.EnterDown, event)
        }
        // Ignore blur's duplicate TextChange so the result row is not rebuilt during its checkbox click.
        let lastHandledCompanySearchText = this.searchTextField.text
        this.searchTextField.controlEventTargetAccumulator.TextChange = () => {
            const currentCompanySearchText = this.searchTextField.text
            if (currentCompanySearchText === lastHandledCompanySearchText) {
                return
            }
            lastHandledCompanySearchText = currentCompanySearchText
            this._companySearchGeneration += 1
            if (this._companySearchDelayTimer !== undefined) {
                clearTimeout(this._companySearchDelayTimer)
                this._companySearchDelayTimer = undefined
            }

            this._selectedCompany = undefined
            this.selectedCompanyLabel.text = "Ettevõte valimata"
            this.searchResultsDataView.data = []
            this._hideError()
            this._updateForCompanySelection()

            if (currentCompanySearchText.trim().length >= 3) {
                this._companySearchDelayTimer = setTimeout(() => {
                    this._companySearchDelayTimer = undefined
                    this._performSearch().then(undefined)
                }, 300)
            }
            this.setNeedsLayout()
        }
        this.cancelButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.dialogView?.dismiss()
        }
        this.administratorCheckbox.controlEventTargetAccumulator.SelectionChange = () => {
            this._selectOnlyRoleCheckbox(this.administratorCheckbox)
        }
        this.procurerCheckbox.controlEventTargetAccumulator.SelectionChange = () => {
            this._selectOnlyRoleCheckbox(this.procurerCheckbox)
        }
        this.offererCheckbox.controlEventTargetAccumulator.SelectionChange = () => {
            this._selectOnlyRoleCheckbox(this.offererCheckbox)
        }
    }


    override wasAddedToViewTree() {
        super.wasAddedToViewTree()
        RHPageFocusManager.shared.pushManagerAsNewLayer(this._focusManager!)
    }


    override wasRemovedFromViewTree() {
        super.wasRemovedFromViewTree()
        if (this._companySearchDelayTimer !== undefined) {
            clearTimeout(this._companySearchDelayTimer)
            this._companySearchDelayTimer = undefined
        }
        this._companySearchGeneration += 1
        RHPageFocusManager.shared.popLayer()
    }


    get selectedCompany(): CompanySearchResult | undefined {
        return this._selectedCompany
    }


    get requestedRole(): RHCompanyMembershipRequestRole | undefined {
        if (this.administratorCheckbox.selected) {
            return "administrator"
        }
        if (this.procurerCheckbox.selected) {
            return "procurer"
        }
        if (this.offererCheckbox.selected) {
            return "offerer"
        }
        return undefined
    }


    _selectOnlyRoleCheckbox(selectedCheckbox: RHCheckbox) {
        if (!selectedCheckbox.selected) {
            return
        }
        [
            this.administratorCheckbox,
            this.procurerCheckbox,
            this.offererCheckbox
        ]
            .filter(checkbox => checkbox !== selectedCheckbox)
            .forEach(checkbox => {
                checkbox.selected = NO
            })
    }


    _applyRoleSelectionVisibility(isVisible: boolean) {
        const roleSelectionViews = [
            this.rolesLabel,
            this.administratorCheckbox,
            this.procurerCheckbox,
            this.offererCheckbox
        ]
        if (isVisible) {
            roleSelectionViews.everyElement.addedAsSubviewToView(this)
        }
        else {
            roleSelectionViews.everyElement.removeFromSuperview()
        }
    }


    showError(message: string) {
        this.warningAlertView.text = message
        this.warningAlertView.addedAsSubviewToView(this)
        this.setNeedsLayout()
    }
    
    
    _hideError() {
        this.warningAlertView.removeFromSuperview()
    }


    _updateForCompanySelection() {
        const company = this._selectedCompany
        if (!company) {
            this._applyRoleSelectionVisibility(YES)
            this._applyProcurerEligibility(YES)
            this.registrationInfoAlertView.removeFromSuperview()
            this.submitButton.titleLabel.text = "Taotle"
            return
        }

        this.submitButton.titleLabel.text =
            RequestCompanyMembershipDialogView._submitTitle(company)
        if (!company.isRegistered) {
            this._applyRoleSelectionVisibility(NO)
            this._applyProcurerEligibility(YES)
            this.registrationInfoAlertView.addedAsSubviewToView(this)
            return
        }

        this._applyRoleSelectionVisibility(YES)
        this._applyProcurerEligibility(company.isEligibleToActAsProcurer)
        this.registrationInfoAlertView.removeFromSuperview()
    }


    /** Forces the procurer radio off when the target company is ineligible, mirroring the block enforced server-side. */
    _applyProcurerEligibility(isEligible: boolean) {
        this.procurerCheckbox.enabled = isEligible
        if (isEligible) {
            this.procurerIneligibleLabel.removeFromSuperview()
            this.procurerCheckbox.hoverText = undefined
        }
        else {
            this.procurerIneligibleLabel.addedAsSubviewToView(this)
            this.procurerCheckbox.hoverText = RHCompanyAdministrationViewTokens.procurerIneligibleExplanation
        }
        if (!isEligible && this.procurerCheckbox.selected) {
            this.procurerCheckbox.selected = NO
        }
        this.setNeedsLayout()
    }


    async _performSearch() {
        if (this._companySearchDelayTimer !== undefined) {
            clearTimeout(this._companySearchDelayTimer)
            this._companySearchDelayTimer = undefined
        }
        const searchGeneration = ++this._companySearchGeneration
        const query = this.searchTextField.text.trim()
        if (query.length < 3) {
            this.showError("Sisesta otsingusse vähemalt kolm tähemärki.")
            return
        }
        try {
            const response = (await SocketClient.SearchCompany(
                { query },
                CBSocketClient.completionPolicy.last
            )).result
            if (
                searchGeneration !== this._companySearchGeneration ||
                query !== this.searchTextField.text.trim()
            ) {
                return
            }
            this._selectedCompany = undefined
            this.selectedCompanyLabel.text = "Ettevõte valimata"
            this._updateForCompanySelection()
            this.searchResultsDataView.data =
                RequestCompanyMembershipDialogView._searchRows(response?.companies ?? [])
            if ((this.searchResultsDataView.data?.length ?? 0) === 0) {
                this.showError("Ettevõtet ei leitud.")
            }
            else {
                this._hideError()
                this.setNeedsLayout()
            }
        }
        catch (error) {
            if (searchGeneration !== this._companySearchGeneration) {
                return
            }
            console.error("Company membership search failed:", error)
            this.showError("Ettevõtte otsimine ebaõnnestus. Palun proovi uuesti.")
        }
    }


    override layoutSubviews() {
        super.layoutSubviews()
        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(padding)
        const dialogTokens = RHCompanyAdministrationViewTokens.membershipRequestDialog
        const footerButtonWidth = this._footerButtonWidth(bounds.width)
        const roleCheckboxes = [this.administratorCheckbox, this.procurerCheckbox, this.offererCheckbox]
        const roleCheckboxWidths = roleCheckboxes.map(checkbox => Math.ceil(
            checkbox.titleLabel.intrinsicContentWidth(RHForms.fieldSingleLineFieldHeight) +
            RHForms.checkboxControlLeadingInset + RHForms.checkboxControlSize +
            RHForms.checkboxControlGap + parseFloat(RHForms.checkboxLabelMarginLeft)
        ))
        const canDisplayRolesInSingleRow =
            roleCheckboxWidths.reduce((totalWidth, checkboxWidth) => totalWidth + checkboxWidth, 0) +
            RHSpacing.medium * (roleCheckboxes.length - 1) <= bounds.width

        bounds
            .rectangleWithHeight(RHButtons.compactHeight)
            .distributeViewsAlongWidth(
                [this.titleLabel, this.explanationButton],
                undefined,
                RHSpacing.small,
                // The smaller navigation label has the same text and can collide in the shared width cache.
                [this.titleLabel.intrinsicContentSizeWithConstraints().width + RHSpacing.tiny, RHButtons.compactHeight]
            )
            .rectangleForNextRow(RHSpacing.medium, RHForms.fieldSingleLineFieldHeight)
            .distributeViewsAlongWidth(
                [this.searchTextField, this.searchButton],
                undefined,
                RHSpacing.small,
                [nil, RHSpacing.xxLarge * 2]
            )
            .rectangleForNextRow(RHSpacing.small, this.selectedCompanyLabel)
            .assignedAsFrameOfView(this.selectedCompanyLabel)
            .rectangleForNextRow(
                RHSpacing.small,
                Math.min(dialogTokens.searchResultsHeight, UIView.pageHeight * 0.5)
            )
            .assignedAsFrameOfView(this.searchResultsDataView)
            .IF(this.rolesLabel.superview === this)
            .rectangleForNextRow(RHSpacing.medium, this.rolesLabel)
            .assignedAsFrameOfView(this.rolesLabel)
            .rectangleForNextRow(RHSpacing.small, RHForms.fieldSingleLineFieldHeight)
            .IF(canDisplayRolesInSingleRow)
            .distributeViewsAlongWidth(roleCheckboxes, undefined, RHSpacing.medium, roleCheckboxWidths)
            .ELSE()
            .assignedAsFrameOfView(this.administratorCheckbox)
            .rectangleForNextRow(RHSpacing.tiny, RHForms.fieldSingleLineFieldHeight)
            .assignedAsFrameOfView(this.procurerCheckbox)
            .rectangleForNextRow(RHSpacing.tiny, RHForms.fieldSingleLineFieldHeight)
            .assignedAsFrameOfView(this.offererCheckbox)
            .ENDIF()
            .IF(this.procurerIneligibleLabel.superview === this)
            .rectangleForNextRow(RHSpacing.tiny, this.procurerIneligibleLabel)
            .assignedAsFrameOfView(this.procurerIneligibleLabel)
            .ENDIF()
            .ENDIF()
            .IF(this.registrationInfoAlertView.superview === this)
            .rectangleForNextRow(RHSpacing.medium, this.registrationInfoAlertView)
            .assignedAsFrameOfView(this.registrationInfoAlertView)
            .ENDIF()
            .IF(this.warningAlertView.superview === this)
            .rectangleForNextRow(RHSpacing.medium, this.warningAlertView)
            .assignedAsFrameOfView(this.warningAlertView)
            .ENDIF()
            .rectangleForNextRow(RHSpacing.medium, dialogTokens.footerButtonHeight)
            .rectangleWithWidth(
                footerButtonWidth * 2 + dialogTokens.footerButtonGap,
                0.5
            )
            .distributeViewsAlongWidth(
                [this.cancelButton, this.submitButton],
                undefined,
                dialogTokens.footerButtonGap,
                [footerButtonWidth, footerButtonWidth]
            )
    }


    _footerButtonWidth(boundsWidth: number) {
        const tokens = RHCompanyAdministrationViewTokens.membershipRequestDialog
        const preferredWidth = [
            this.cancelButton.titleLabel.intrinsicContentWidth(tokens.footerButtonHeight),
            this.submitButton.titleLabel.intrinsicContentWidth(tokens.footerButtonHeight)
        ].max()
            + tokens.footerButtonContentPadding * 2
            + tokens.footerButtonTextSafetyBuffer
        const availableButtonWidth = Math.max(
            0,
            (boundsWidth - tokens.footerButtonGap) * 0.5
        )

        return [
            preferredWidth.constrainedValue(
                tokens.footerButtonMinWidth,
                tokens.footerButtonMaxWidth
            ),
            availableButtonWidth
        ].min()
    }


    override intrinsicContentWidth() {
        return RHCompanyAdministrationViewTokens.membershipRequestDialog.maxWidth
    }


    static _registrationExplanation(): string {
        return (
            "Ettevõte ei ole veel keskkonnas registreeritud. " +
            "Registreerimistaotluse kinnitamise järel lisatakse ettevõte ja " +
            "sinust saab selle peakasutaja. Kinnituskiri saadetakse " +
            "registriandmetes märgitud ettevõtte ametlikule e-posti aadressile."
        )
    }


    static _submitTitle(
        company: Pick<CompanySearchResult, "isRegistered">
    ): string {
        if (company.isRegistered) {
            return "Taotle"
        }
        return "Registreeri ettevõte"
    }


    static _searchRows(
        companies: CompanySearchResult[]
    ): CompanySearchRow[] {
        return companies.map(company => ({
            ...company,
            registrationStatusText: RequestCompanyMembershipDialogView._registrationStatusText(company)
        }))
    }


    static _registrationStatusText(
        company: Pick<CompanySearchResult, "isRegistered">
    ): string {
        if (company.isRegistered) {
            return "Registreeritud"
        }
        return "Registreerimata"
    }

}


class CompanyInvitationDecisionDialogView extends UIView {

    readonly titleLabel = new UITextView(nil, UITextView.type.paragraph)
        .configuredWithObject({
            text: "Ettevõtte kutse",
            textColor: RHColors.textHeading,
            style: {
                fontSize: RHFont.sizeLarge,
                fontWeight: RHFont.weightSemibold
            }
        })
        .addedAsSubviewToView(this)

    readonly explanationLabel = new UITextView()
        .configuredWithObject({
            textColor: RHColors.textSecondary,
            isSingleLine: NO
        })
        .addedAsSubviewToView(this)

    readonly cancelButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Sulge", style: { fontSize: RHButtons.compactFontSize } },
            variant: "secondary",
            contentPadding: RHButtons.compactContentPadding
        })
        .addedAsSubviewToView(this)

    readonly rejectButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Lükka tagasi", style: { fontSize: RHButtons.compactFontSize } },
            variant: RHButtons.destructiveUtilityVariant,
            contentPadding: RHButtons.compactContentPadding
        })
        .addedAsSubviewToView(this)

    readonly acceptButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Võta vastu", style: { fontSize: RHButtons.compactFontSize } },
            variant: "success",
            contentPadding: RHButtons.compactContentPadding
        })
        .addedAsSubviewToView(this)

    override _focusManager = new RHFocusManager(() => [
        this.cancelButton,
        this.rejectButton,
        this.acceptButton
    ])


    constructor(elementID?: string) {
        super(elementID)
        this.backgroundColor = UIColor.whiteColor
        this.cancelButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.dialogView?.dismiss()
        }
    }


    override wasAddedToViewTree() {
        super.wasAddedToViewTree()
        RHPageFocusManager.shared.pushManagerAsNewLayer(this._focusManager!)
    }


    override wasRemovedFromViewTree() {
        super.wasRemovedFromViewTree()
        RHPageFocusManager.shared.popLayer()
    }


    configureForWorkflow(workflow: RHCompanyMembershipWorkflow) {
        const roleLabel = CompanyRoleBadgesView
            .displayLabelsForRoles(workflow.requestedRoles)
            .join(", ")
        this.explanationLabel.text =
            `Ettevõte „${workflow.company.name}“ kutsus sind enda liikmeks rollis „${roleLabel}“.`
    }


    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(this.core.paddingLength)

        bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(RHSpacing.medium, this.explanationLabel)
            .assignedAsFrameOfView(this.explanationLabel)
            .rectangleForNextRow(RHSpacing.large, RHButtons.compactHeight)
            .distributeViewsEquallyAlongWidth(
                [this.cancelButton, this.rejectButton, this.acceptButton],
                RHSpacing.medium
            )
    }


    override intrinsicContentWidth() {
        return 540
    }


    override intrinsicContentHeight() {
        return 210
    }

}


export class CompanyAffiliationViewController extends UIViewController {

    static workflowTypeText(
        workflow: Pick<RHCompanyMembershipWorkflow, "direction" | "kind">
    ): string {
        if (workflow.kind === "registration") {
            return "Ettevõtte registreerimine"
        }
        if (workflow.direction === "company_to_user") {
            return "Mind kutsuti"
        }
        return "Mina taotlesin"
    }


    static workflowStatusText(
        workflow: Pick<RHCompanyMembershipWorkflow, "direction" | "status" | "expiresAt">
    ): string {
        if (workflow.status === "approved") {
            return "Kinnitatud"
        }
        if (workflow.status === "rejected") {
            return "Tagasilükatud"
        }
        if (workflow.status === "cancelled") {
            return "Tühistatud"
        }
        if (workflow.status === "expired") {
            return "Aegunud"
        }
        let expiryText = ""
        if (workflow.expiresAt) {
            expiryText = ` (${CompanyAffiliationViewController.expiryCountdownText(workflow.expiresAt)})`
        }
        if (workflow.direction === "user_to_company") {
            return `Esitatud${expiryText}`
        }
        return `Ootel${expiryText}`
    }


    // Rounds up so "expires in under an hour" never reads as "0 hours left".
    static expiryCountdownText(expiresAt: Date | string): string {
        const millisecondsRemaining = new Date(expiresAt).getTime() - Date.now()
        if (millisecondsRemaining <= 0) {
            return "on aegunud"
        }
        const hoursRemaining = Math.ceil(millisecondsRemaining / (1000 * 60 * 60))
        if (hoursRemaining < 24) {
            return `aegub ${hoursRemaining} tunni pärast`
        }
        const daysRemaining = Math.ceil(millisecondsRemaining / (1000 * 60 * 60 * 24))
        return `aegub ${daysRemaining} päeva pärast`
    }


    readonly titleLabel = new UITextView(this.view.elementID + "TitleLabel", UITextView.type.header4)
        .configuredWithObject({
            text: "Ettevõte",
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textPrimary
        })
        .addedAsSubviewToView(this.view)

    readonly explanationLabel = new UITextView()
        .configuredWithObject({
            text: "Siin saad hallata ettevõtteid, kutseid, taotlusi ja liikmeid.",
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textSecondary,
            isSingleLine: NO
        })
        .addedAsSubviewToView(this.view)

    readonly addCompanyButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Seo ennast ettevõttega" },
            variant: "success"
        })
        .addedAsSubviewToView(this.view)

    readonly contractingAuthoritySettingsView = new CompanyContractingAuthoritySettingsView()

    readonly companyMembersView = new CompanyAdministrationMembersView(
        this.view.elementID + "CompanyMembersView"
    )
        .addedAsSubviewToView(this.view)

    readonly noCompanyExplanationView = new RHAlertBannerView()
        .configuredWithObject({
            alertType: RHAlertBannerView.type.Warning,
            textAlignment: UITextView.textAlignment.center
        })
        .addedAsSubviewToView(this.view)

    readonly membershipInvitationCalloutView = new RHCalloutView(
        "info",
        "Ettevõtte liikmekutse",
        ""
    )

    readonly workflowsTitleLabel = new UITextView()
        .configuredWithObject({
            text: "Taotlused ja kutsed",
            textColor: RHColors.textHeading,
            style: {
                fontSize: RHFont.sizeLarge,
                fontWeight: RHFont.weightSemibold
            }
        })
        .addedAsSubviewToView(this.view)

    readonly workflowsView = new CompanyAffiliationWorkflowsView()
        .addedAsSubviewToView(this.view)

    override _focusManager = new RHFocusManager(() => {
        const items: (UIView | RHFocusManager)[] = [this.addCompanyButton]
        if (this.contractingAuthoritySettingsView.superview === this.view) {
            items.push(this.contractingAuthoritySettingsView._focusManager!)
        }
        if (this.companyMembersView.superview === this.view) {
            items.push(this.companyMembersView._focusManager!)
        }
        if (this.workflowsView.superview === this.view) {
            items.push(this.workflowsView._focusManager!)
        }
        return items
    })

    _allWorkflows: RHCompanyMembershipWorkflow[] = []
    _activeCompanyAffiliation?: RHCompanyAffiliation
    _requestedCompanyId?: string
    _requestedWorkflowId?: string
    _handledWorkflowId?: string
    _loadGeneration = 0
    _settingsCompanyId?: string
    workflowLinkWasConsumed?: () => void


    constructor(view: UIView) {
        super(view)
        this.contractingAuthoritySettingsView.saveButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._saveContractingAuthorityTypes().then(undefined)
        }
        this.companyMembersView.membersDidChange = () => {
            this._refreshAfterMutation().then(undefined)
        }
        this.addCompanyButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            UIRoute.currentRoute
                .routeWithViewControllerComponent(
                    CompanyMembershipRequestViewController,
                    {}
                )
                .apply()
        }
        this.workflowsView.invitationWasAccepted = workflow => {
            this._acceptInvitation(workflow).then(undefined)
        }
        this.workflowsView.invitationWasRejected = workflow => {
            this._rejectInvitation(workflow)
        }
        this.workflowsView.membershipRequestWasCancelled = workflow => {
            this._cancelMembershipRequest(workflow)
        }
    }


    set requestedWorkflowId(workflowId: string | undefined) {
        this._requestedWorkflowId = workflowId
        if (!workflowId) {
            this._handledWorkflowId = undefined
        }
        if (
            workflowId &&
            workflowId !== this._handledWorkflowId &&
            this.view.superview
        ) {
            this._load(YES).then(undefined)
        }
    }


    set requestedCompanyId(companyId: string | undefined) {
        this._requestedCompanyId = companyId
    }


    override async viewWillAppear() {
        await super.viewWillAppear()
        RHPageFocusManager.shared.registerContentControllerManager(
            this._focusManager!,
            { focusesFirstItem: NO }
        )
    }


    override async viewWillDisappear() {
        await super.viewWillDisappear()
        RHPageFocusManager.shared.deregisterContentControllerManager(this._focusManager!)
    }


    override async viewDidAppear() {
        await super.viewDidAppear()
        let shouldRefreshCompanyData = NO
        if (this._requestedWorkflowId) {
            shouldRefreshCompanyData = YES
        }
        await this._load(shouldRefreshCompanyData)
    }


    override viewDidReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.viewDidReceiveBroadcastEvent(event)
        if (event.name === RHCore.broadcastEventName.applicationModeDidChange) {
            const affiliation = RHCore.sharedInstance.companyAffiliationStore
                .activeCompanyAffiliation
            this._activeCompanyAffiliation = affiliation
            this._applyContractingAuthorityTypeVisibility(affiliation)
        }
        if (
            event.name === RHCore.broadcastEventName.companyAffiliationsDidChange ||
            event.name === RHCore.broadcastEventName.activeCompanyDidChange ||
            event.name === RHCore.broadcastEventName.currentCompanyAffiliationDidChange
        ) {
            this._load().then(undefined)
        }
    }


    async _load(shouldRefreshCompanyData: boolean = NO) {
        const loadGeneration = ++this._loadGeneration
        this.view.loading = YES
        try {
            const result = await RHCore.sharedInstance.companyAffiliationStore
                .retrieveMyCompanyMembershipsAndRequests(shouldRefreshCompanyData)
            if (loadGeneration !== this._loadGeneration) {
                return
            }
            const activeCompanyName = RHCore.sharedInstance.companyAffiliationStore
                .activeCompanyAffiliation?.company?.name
            this.titleLabel.text = activeCompanyName
                ? `Ettevõte: ${activeCompanyName}`
                : "Ettevõte"
            const activeCompanyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
            const activeCompanyAffiliation = RHCore.sharedInstance.companyAffiliationStore
                .activeCompanyAffiliation
            this._activeCompanyAffiliation = activeCompanyAffiliation
            if (this._settingsCompanyId !== activeCompanyId || !this.contractingAuthoritySettingsView.hasUnsavedChanges) {
                this._settingsCompanyId = activeCompanyId
                this.contractingAuthoritySettingsView.applySavedTypes(
                    RHContractingAuthorityRules.allowedTypesForCompany(activeCompanyAffiliation?.company ?? {})
                )
            }
            this._applyContractingAuthorityTypeVisibility(activeCompanyAffiliation)
            const currentUserProfile = RHCore.sharedInstance.userProfile
            const shouldShowMemberList = !!(
                activeCompanyAffiliation &&
                activeCompanyAffiliation.source !== "relationship" &&
                activeCompanyAffiliation.membershipStatus === "active"
            )
            const canViewMemberList = !!(
                activeCompanyAffiliation?.canViewAllUsers === YES &&
                (
                    activeCompanyAffiliation.isAdministrator === YES ||
                    activeCompanyAffiliation.roles.includes("procurer") ||
                    activeCompanyAffiliation.roles.includes("offerer")
                )
            )
            let currentUserMember: RHCompanyMember | undefined
            if (
                shouldShowMemberList &&
                activeCompanyAffiliation &&
                activeCompanyId &&
                currentUserProfile?._id
            ) {
                currentUserMember = {
                    userId: currentUserProfile._id,
                    name: currentUserProfile.contactInformation?.name ?? currentUserProfile.email,
                    email: currentUserProfile.email,
                    roles: activeCompanyAffiliation.roles,
                    status: "active",
                    companies: [{
                        _id: activeCompanyId,
                        name: activeCompanyAffiliation.company?.name ?? ""
                    }]
                }
            }

            let companyMembers: RHCompanyMember[] = []
            if (
                shouldShowMemberList &&
                canViewMemberList &&
                activeCompanyId
            ) {
                companyMembers = await this._retrieveCompanyMembersForCompanyId(activeCompanyId)
                if (
                    loadGeneration !== this._loadGeneration ||
                    RHCore.sharedInstance.companyAffiliationStore.activeCompanyId !== activeCompanyId
                ) {
                    return
                }
            }
            if (currentUserMember) {
                const currentUserMemberId = currentUserMember.userId
                if (!companyMembers.some(member => member.userId === currentUserMemberId)) {
                    companyMembers.unshift(currentUserMember)
                }
            }

            this.companyMembersView.companyId = activeCompanyId ?? ""
            this.companyMembersView.administratorCount = companyMembers.filter(
                member => member.roles.includes("administrator")
            ).length
            this.companyMembersView.isEligibleToActAsProcurer =
                activeCompanyAffiliation?.isEligibleToActAsProcurer !== NO
            this.companyMembersView.currentUserId = currentUserProfile?._id
            this.companyMembersView.allowsMemberManagement =
                activeCompanyAffiliation?.isAdministrator === YES
            this.companyMembersView.isCurrentUserLeaveButtonEnabled = NO
            this.companyMembersView.currentUserLeaveButtonHoverText = undefined
            this.companyMembersView.currentUserLeaveWasPressed = undefined
            if (activeCompanyAffiliation && activeCompanyId) {
                const leaveAction = CompanyAffiliationViewController._leaveActionForAffiliation(
                    activeCompanyAffiliation
                )
                if (leaveAction === "explainSoleAdministrator") {
                    this.companyMembersView.isCurrentUserLeaveButtonEnabled = YES
                    this.companyMembersView.currentUserLeaveButtonHoverText =
                        RHCompanyAdministrationViewTokens.soleAdministratorLeaveExplanation
                    this.companyMembersView.currentUserLeaveWasPressed = () => {
                        CBDialogViewShower.alert(
                            RHCompanyAdministrationViewTokens.soleAdministratorLeaveExplanation
                        )
                    }
                }
                else if (leaveAction === "leave") {
                    this.companyMembersView.isCurrentUserLeaveButtonEnabled = YES
                    this.companyMembersView.currentUserLeaveWasPressed = () => {
                        this._confirmAndLeaveCompany(activeCompanyAffiliation, activeCompanyId)
                    }
                }
                else {
                    this.companyMembersView.currentUserLeaveButtonHoverText =
                        "Seda ettevõtte seost ei saa praegu lõpetada."
                }
            }
            this._allWorkflows = result.workflows
            this.companyMembersView.members = companyMembers
            this.workflowsView.workflows = result.workflows.filter(workflow => workflow.kind !== "registration")
            const noCompanyExplanation =
                CompanyAffiliationViewController._noActiveCompanyExplanation(result.workflows)
            this.noCompanyExplanationView.text = noCompanyExplanation.text
            this.membershipInvitationCalloutView.bodyText = noCompanyExplanation.text
            if (noCompanyExplanation.tone === "warning") {
                this.noCompanyExplanationView.alertType = RHAlertBannerView.type.Warning
            }
            this._applyCompanyOverviewVisibility(
                !!activeCompanyId,
                noCompanyExplanation.tone === "info"
            )
            this._applyRepresentativeWorkflowsVisibility(
                activeCompanyAffiliation?.isAdministrator !== YES
            )
            this._openRequestedWorkflowIfAvailable()
        }
        catch (error) {
            console.error("Company memberships failed to load:", error)
            CBDialogViewShower.alert("Ettevõtete laadimine ebaõnnestus.")
        }
        finally {
            if (loadGeneration === this._loadGeneration) {
                this.view.loading = NO
                this.view.setNeedsLayoutUpToRootView()
            }
        }
    }


    async _retrieveCompanyMembersForCompanyId(
        companyId: string
    ): Promise<RHCompanyMember[]> {
        try {
            const response = await SocketClient.ListCompanyMembersAndPending({ companyId })
            if (!response.result?.success) {
                throw new Error(
                    RHCore.sharedInstance.errorMessageFromResponse(
                        response,
                        "Liikmete laadimine ebaõnnestus."
                    ) ?? "Liikmete laadimine ebaõnnestus."
                )
            }
            return response.result.members
        }
        catch (error) {
            console.error("Company members failed to load:", error)
            CBDialogViewShower.alert("Liikmete laadimine ebaõnnestus.")
            return []
        }
    }


    // Company/workflow "_id" fields are typed as Mongoose's Types.ObjectId, but arrive over
    // the socket as a plain string once JSON-serialized - this normalizes either shape.
    static _companyIdString(
        rawCompanyId: string | { toString(): string } | undefined
    ): string | undefined {
        if (!rawCompanyId) {
            return undefined
        }
        if (typeof rawCompanyId === "string") {
            return rawCompanyId
        }
        const stringValue = rawCompanyId.toString()
        if (stringValue !== "[object Object]") {
            return stringValue
        }
        return undefined
    }


    _openRequestedWorkflowIfAvailable() {
        const workflowId = this._requestedWorkflowId
        if (!workflowId || workflowId === this._handledWorkflowId) {
            return
        }
        this._handledWorkflowId = workflowId
        this.workflowLinkWasConsumed?.()
        const workflow = this._allWorkflows.find(candidate => candidate.requestId === workflowId)
        if (!workflow) {
            CBDialogViewShower.alert(
                "Taotlus või kutse on aegunud või pole enam saadaval."
            )
            return
        }
        const workflowCompanyId = CompanyAffiliationViewController._companyIdString(
            workflow.company?._id
        )
        if (this._requestedCompanyId && workflowCompanyId !== this._requestedCompanyId) {
            CBDialogViewShower.alert(
                "See kutse või taotlus ei kuulu lingis määratud ettevõttele."
            )
            return
        }
        if (workflow.status !== "pending") {
            CBDialogViewShower.alert(
                CompanyAffiliationViewController._workflowLinkStatusMessage(workflow)
            )
            return
        }
        if (workflow.direction === "user_to_company") {
            CBDialogViewShower.alert(
                CompanyAffiliationViewController._workflowLinkStatusMessage(workflow)
            )
            return
        }

        const dialogView = new CompanyInvitationDecisionDialogView()
        dialogView.configureForWorkflow(workflow)
        dialogView.acceptButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            dialogView.dialogView?.dismiss()
            await this._acceptInvitation(workflow)
        }
        dialogView.rejectButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            dialogView.dialogView?.dismiss()
            await this._performRejectInvitation(workflow)
        }
        CBDialogViewShower.showDialog(dialogView, undefined, NO)
    }


    _confirmAndLeaveCompany(
        affiliation: RHCompanyAffiliation,
        companyId: string
    ) {
        const shower = CBDialogViewShower.showQuestionDialog(
            `Kas soovid lõpetada seose ettevõttega „${RHTextSanitizer.escapedHTML(affiliation.company?.name ?? "")}“?`
        )
        shower.yesButtonWasPressed = async () => {
            shower.dialogView.dismiss()
            try {
                const response = await SocketClient.LeaveCompany({ companyId })
                if (response.result?.success) {
                    await this._refreshAfterMutation()
                }
                else {
                    this._showMutationError(response, "Ettevõttest lahkumine ebaõnnestus.")
                }
            }
            catch (error) {
                console.error("Leave company failed:", error)
                CBDialogViewShower.alert("Ettevõttest lahkumine ebaõnnestus.")
            }
        }
    }


    async _acceptInvitation(workflow: RHCompanyMembershipWorkflow) {
        try {
            const response = await SocketClient.AcceptCompanyInvitation({
                requestId: workflow.requestId
            })
            if (response.result?.success) {
                await this._refreshAfterMutation()
            }
            else {
                this._showMutationError(response, "Kutse vastuvõtmine ebaõnnestus.")
            }
        }
        catch (error) {
            console.error("Accept company invitation failed:", error)
            CBDialogViewShower.alert("Kutse vastuvõtmine ebaõnnestus.")
        }
    }


    _rejectInvitation(workflow: RHCompanyMembershipWorkflow) {
        const shower = CBDialogViewShower.showQuestionDialog(
            `Kas soovid ettevõtte „${RHTextSanitizer.escapedHTML(workflow.company.name)}“ kutse tagasi lükata?`
        )
        shower.yesButtonWasPressed = async () => {
            shower.dialogView.dismiss()
            await this._performRejectInvitation(workflow)
        }
    }


    async _performRejectInvitation(workflow: RHCompanyMembershipWorkflow) {
        try {
            const response = await SocketClient.RejectCompanyInvitation({
                requestId: workflow.requestId
            })
            if (response.result?.success) {
                await this._refreshAfterMutation()
            }
            else {
                this._showMutationError(response, "Kutse tagasilükkamine ebaõnnestus.")
            }
        }
        catch (error) {
            console.error("Reject company invitation failed:", error)
            CBDialogViewShower.alert("Kutse tagasilükkamine ebaõnnestus.")
        }
    }


    _cancelMembershipRequest(workflow: RHCompanyMembershipWorkflow) {
        const shower = CBDialogViewShower.showQuestionDialog(
            `Kas soovid ettevõttele „${RHTextSanitizer.escapedHTML(workflow.company.name)}“ saadetud taotluse tühistada?`
        )
        shower.yesButtonWasPressed = async () => {
            shower.dialogView.dismiss()
            try {
                const response = await SocketClient.CancelCompanyMembershipRequest({
                    requestId: workflow.requestId
                })
                if (response.result?.success) {
                    await this._refreshAfterMutation()
                }
                else {
                    this._showMutationError(response, "Taotluse tühistamine ebaõnnestus.")
                }
            }
            catch (error) {
                console.error("Cancel company membership request failed:", error)
                CBDialogViewShower.alert("Taotluse tühistamine ebaõnnestus.")
            }
        }
    }


    async _refreshAfterMutation() {
        try {
            await RHCore.sharedInstance.refreshCurrentCompanyAffiliation()
            await this._load()
        }
        catch (error) {
            console.error("Company view refresh after successful mutation failed:", error)
            CBDialogViewShower.alert(
                "Toiming õnnestus, kuid vaate värskendamine ebaõnnestus. " +
                "Palun värskenda lehte."
            )
        }
    }


    async _saveContractingAuthorityTypes() {
        const settingsView = this.contractingAuthoritySettingsView
        const contractingAuthorityTypes = settingsView.selectedTypes
        const companyId = CompanyAffiliationViewController._companyIdString(
            (this._activeCompanyAffiliation?.company)?._id
        )
        if (!companyId || !settingsView.saveButton.enabled || contractingAuthorityTypes.length === 0) {
            return
        }
        settingsView.isSaving = YES
        try {
            const response = await SocketClient.SaveCompanyProcurementSettings({
                companyId,
                contractingAuthorityTypes
            })
            if (!response.result?.success) {
                CBDialogViewShower.alert(
                    RHCore.sharedInstance.errorMessageFromResponse(
                        response,
                        "Hankija määratluse salvestamine ebaõnnestus."
                    ) ?? "Hankija määratluse salvestamine ebaõnnestus."
                )
                return
            }
            if (this._settingsCompanyId === companyId) {
                settingsView.applySavedTypes(response.result.company?.contractingAuthorityTypes ?? contractingAuthorityTypes)
            }
            await this._refreshAfterMutation()
            CBDialogViewShower.alert("Hankija määratlus valitud")
        }
        catch (error) {
            console.error("Company contracting authority type failed to save:", error)
            CBDialogViewShower.alert("Hankija määratluse salvestamine ebaõnnestus.")
        }
        finally {
            settingsView.isSaving = NO
        }
    }


    _applyContractingAuthorityTypeVisibility(
        affiliation: RHCompanyAffiliation | undefined
    ) {
        const shouldShowContractingAuthorityType = !!(
            affiliation &&
            affiliation.source !== "relationship" &&
            affiliation.membershipStatus === "active" &&
            affiliation.isAdministrator === YES &&
            RHCore.sharedInstance.applicationMode === "procurer"
        )
        if (shouldShowContractingAuthorityType) {
            this.contractingAuthoritySettingsView.addedAsSubviewToView(this.view)
        }
        else {
            this.contractingAuthoritySettingsView.removeFromSuperview()
        }
        this.view.setNeedsLayoutUpToRootView()
    }


    _applyCompanyOverviewVisibility(
        hasActiveCompany: boolean,
        hasMembershipInvitation: boolean
    ) {
        const showsMembershipInvitation = !hasActiveCompany && hasMembershipInvitation
        if (hasActiveCompany) {
            if (this.companyMembersView.superview !== this.view) {
                this.companyMembersView.addedAsSubviewToView(this.view)
            }
            this.noCompanyExplanationView.removeFromSuperview()
            this.membershipInvitationCalloutView.removeFromSuperview()
        }
        else if (showsMembershipInvitation) {
            if (this.membershipInvitationCalloutView.superview !== this.view) {
                this.membershipInvitationCalloutView.addedAsSubviewToView(this.view)
            }
            this.companyMembersView.removeFromSuperview()
            this.noCompanyExplanationView.removeFromSuperview()
        }
        else {
            if (this.noCompanyExplanationView.superview !== this.view) {
                this.noCompanyExplanationView.addedAsSubviewToView(this.view)
            }
            this.companyMembersView.removeFromSuperview()
            this.membershipInvitationCalloutView.removeFromSuperview()
        }
        this.view.setNeedsLayoutUpToRootView()
    }


    _applyRepresentativeWorkflowsVisibility(isVisible: boolean) {
        if (isVisible) {
            if (this.workflowsTitleLabel.superview !== this.view) {
                this.workflowsTitleLabel.addedAsSubviewToView(this.view)
            }
            if (this.workflowsView.superview !== this.view) {
                this.workflowsView.addedAsSubviewToView(this.view)
            }
        }
        else {
            this.workflowsTitleLabel.removeFromSuperview()
            this.workflowsView.removeFromSuperview()
        }
        this.view.setNeedsLayout()
    }


    _showMutationError(response: { errorResult?: unknown }, fallback: string) {
        CBDialogViewShower.alert(
            RHCore.sharedInstance.errorMessageFromResponse(response, fallback) ?? fallback
        )
    }


    override layoutViewSubviews() {
        super.layoutViewSubviews()
        const bounds = this.view.contentBoundsWithInset(this.core.paddingLength)

        const titleRow = bounds.rectangleWithHeight(this.titleLabel)
        titleRow.assignedAsFrameOfView(this.titleLabel)
        const addCompanyButtonWidth = [
            RHSpacing.xxLarge * 3,
            this.addCompanyButton.titleLabel.intrinsicContentWidth(
                RHButtons.compactPrimaryHeight
            ) + this.addCompanyButton.contentPadding * 2 + RHSpacing.small
        ].max()
        this.addCompanyButton.frame = titleRow
            .rectangleWithWidth(addCompanyButtonWidth, 1)
            .rectangleWithHeight(RHButtons.compactPrimaryHeight, 0.5)

        titleRow
            .rectangleForNextRow(RHSpacing.small, this.explanationLabel)
            .assignedAsFrameOfView(this.explanationLabel)
            .IF(this.contractingAuthoritySettingsView.superview === this.view)
            .rectangleForNextRow(RHSpacing.large, this.contractingAuthoritySettingsView)
            .assignedAsFrameOfView(this.contractingAuthoritySettingsView)
            .ENDIF()
            .IF(this.companyMembersView.superview === this.view)
            .rectangleForNextRow(RHSpacing.large, this.companyMembersView)
            .assignedAsFrameOfView(this.companyMembersView)
            .ELSE()
            .IF(this.membershipInvitationCalloutView.superview === this.view)
            .rectangleForNextRow(RHSpacing.large, this.membershipInvitationCalloutView)
            .assignedAsFrameOfView(this.membershipInvitationCalloutView)
            .ELSE()
            .rectangleForNextRow(RHSpacing.large, this.noCompanyExplanationView)
            .assignedAsFrameOfView(this.noCompanyExplanationView)
            .ENDIF()
            .ENDIF()
            .IF(this.workflowsView.superview === this.view)
            .rectangleForNextRow(RHSpacing.large, this.workflowsTitleLabel)
            .assignedAsFrameOfView(this.workflowsTitleLabel)
            .rectangleForNextRow(
                RHSpacing.small,
                RHCompanyAdministrationViewTokens.pendingWorkflowsTableHeight
            )
            .assignedAsFrameOfView(this.workflowsView)
            .ENDIF()
    }


    // The "no active company" banner must never contradict a pending workflow shown in the
    // table below it, so it derives from the same workflow list rather than a fixed message.
    static _noActiveCompanyExplanation(
        workflows: readonly Pick<RHCompanyMembershipWorkflow, "kind" | "direction" | "status" | "company" | "expiresAt">[]
    ): CompanyAffiliationNoActiveCompanyExplanation {
        const pendingIncomingInvitation = workflows.find(workflow =>
            workflow.status === "pending" && workflow.direction === "company_to_user"
        )
        if (pendingIncomingInvitation) {
            return {
                text: `Ettevõte „${pendingIncomingInvitation.company.name}“ kutsus sind liituma. ` +
                    "Vaata kutset allpool.",
                tone: "info"
            }
        }
        const pendingOutgoingRequest = workflows.find(workflow =>
            workflow.status === "pending" && workflow.direction === "user_to_company"
        )
        if (pendingOutgoingRequest?.kind === "registration") {
            let expiryText = ""
            if (pendingOutgoingRequest.expiresAt) {
                expiryText = ` (${CompanyAffiliationViewController.expiryCountdownText(pendingOutgoingRequest.expiresAt)})`
            }
            return {
                text: `Ettevõtte „${pendingOutgoingRequest.company.name}“ registreerimine on ootel ` +
                    "— kinnituskiri on saadetud ettevõtte ametlikule e-posti aadressile" +
                    expiryText + ".",
                tone: "warning"
            }
        }
        if (pendingOutgoingRequest) {
            let expiryText = ""
            if (pendingOutgoingRequest.expiresAt) {
                expiryText = ` (${CompanyAffiliationViewController.expiryCountdownText(pendingOutgoingRequest.expiresAt)})`
            }
            return {
                text: `Sinu liitumistaotlus ettevõttele „${pendingOutgoingRequest.company.name}“ on ootel` +
                    expiryText + ".",
                tone: "warning"
            }
        }
        return {
            text: "Sa ei ole veel ühegi ettevõttega seotud. Ettevõtte registreerimiseks või " +
                "olemasolevaga liitumiseks vajuta ülal „Seo ennast ettevõttega“.",
            tone: "warning"
        }
    }


    static _leaveActionForAffiliation(
        affiliation: Pick<
            RHCompanyAffiliation,
            "source" | "isAdministrator" | "canLeaveCompany"
        >
    ): "leave" | "explainSoleAdministrator" | "unavailable" {
        if (affiliation.canLeaveCompany) {
            return "leave"
        }
        if (affiliation.source !== "relationship" && affiliation.isAdministrator) {
            return "explainSoleAdministrator"
        }
        return "unavailable"
    }


    static _workflowLinkStatusMessage(
        workflow: Pick<RHCompanyMembershipWorkflow, "direction" | "status">
    ): string {
        let typeText = "Taotlus"
        if (workflow.direction === "company_to_user") {
            typeText = "Kutse"
        }
        const statusText = CompanyAffiliationViewController.workflowStatusText(workflow).toLocaleLowerCase("et")
        return `${typeText} on ${statusText}.`
    }

}


export class CompanyMembershipRequestViewController extends RHDetailsDialogViewController {

    static override readonly routeComponentName = "companyMembershipRequest"
    static override readonly ParameterIdentifierName = {}

    protected override readonly maxWidth =
        RHCompanyAdministrationViewTokens.membershipRequestDialog.maxWidth
    protected override readonly widthViewportRatio =
        RHCompanyAdministrationViewTokens.membershipRequestDialog.widthViewportRatio

    readonly requestView = new RequestCompanyMembershipDialogView()
        .addedAsSubviewToView(this.view)
    protected override readonly contentView = this.requestView


    constructor(view: UIView) {
        super(view)
        this.view.configureWithObject({
            backgroundColor: UIColor.transparentColor
        })

        this.requestView.cancelButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            UIRoute.currentRoute
                .routeByRemovingComponentNamed(
                    CompanyMembershipRequestViewController.routeComponentName
                )
                .apply()
        }
        this.requestView.submitButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            await this._performActionForSelectedCompany()
        }
        this.view.controlEventTargetAccumulator.PointerTap = (_sender: UIView, event: Event) => {
            if (event.target === this.view.viewHTMLElement) {
                UIRoute.currentRoute
                    .routeByRemovingComponentNamed(
                        CompanyMembershipRequestViewController.routeComponentName
                    )
                    .apply()
            }
        }
    }


    async _performActionForSelectedCompany() {
        const company = this.requestView.selectedCompany
        if (!company) {
            CBDialogViewShower.showContentAlert(
                new RHValidationIssuesDialogView([{
                    focusTarget: this.requestView.searchTextField,
                    message: "Vali ettevõte."
                }]),
                () => this.requestView.searchTextField.focus()
            )
            return
        }

        let companyId: string | undefined
        let requestedRole: RHCompanyMembershipRequestRole | undefined
        if (company.isRegistered) {
            companyId = CompanyMembershipRequestViewController._companyIdString(company._id)
            requestedRole = this.requestView.requestedRole
            if (!companyId) {
                this.requestView.showError("Valitud ettevõtte andmed on puudulikud.")
                return
            }
            if (!requestedRole) {
                CBDialogViewShower.showContentAlert(
                    new RHValidationIssuesDialogView([{
                        focusTarget: this.requestView.administratorCheckbox,
                        message: "Vali soovitud roll."
                    }]),
                    () => this.requestView.administratorCheckbox.focus()
                )
                return
            }
        }

        if (!company.isRegistered) {
            await this._previewAndConfirmCompanyRegistration(company)
            return
        }

        this.requestView.loading = YES
        try {
            if (!companyId || !requestedRole) {
                this.requestView.showError("Valitud ettevõtte andmed on puudulikud.")
                return
            }
            const response = await SocketClient.RequestCompanyMembership({
                companyId,
                requestedRole
            })
            if (response.result?.success) {
                UIRoute.currentRoute
                    .routeByRemovingComponentNamed(
                        CompanyMembershipRequestViewController.routeComponentName
                    )
                    .apply()
                CBDialogViewShower.alert("Liitumistaotlus on saadetud.")
                await RHCore.sharedInstance.refreshCurrentCompanyAffiliation()
            }
            else {
                this.requestView.showError(
                    RHCore.sharedInstance.errorMessageFromResponse(
                        response,
                        "Liitumistaotluse saatmine ebaõnnestus."
                    ) ?? "Liitumistaotluse saatmine ebaõnnestus."
                )
            }
        }
        catch (error) {
            console.error("Company membership request failed:", error)
            this.requestView.showError("Liitumistaotluse saatmine ebaõnnestus.")
        }
        finally {
            this.requestView.loading = NO
        }
    }


    // Company "_id" fields are typed as Mongoose's Types.ObjectId, but arrive over the
    // socket as a plain string once JSON-serialized - this normalizes either shape.
    static _companyIdString(
        rawCompanyId: string | { toString(): string } | undefined
    ): string | undefined {
        if (!rawCompanyId) {
            return undefined
        }
        if (typeof rawCompanyId === "string") {
            return rawCompanyId
        }
        const stringValue = rawCompanyId.toString()
        if (stringValue !== "[object Object]") {
            return stringValue
        }
        return undefined
    }


    // Registering a company sends a confirmation email to an address the requester does not
    // control (the company's own official registry email) and cannot be undone once sent, so
    // the user must see exactly which address that is and explicitly confirm before it goes
    // out - this preview step makes no request and sends nothing.
    async _previewAndConfirmCompanyRegistration(company: CompanySearchResult) {
        this.requestView.loading = YES
        try {
            const response = await SocketClient.RetrieveCompanyRegistrationPreview({
                businessRegistryCode: company.businessRegistryCode
            })
            if (!response.result?.success) {
                this.requestView.showError(
                    RHCore.sharedInstance.errorMessageFromResponse(
                        response,
                        "Ettevõtte andmete kontrollimine ebaõnnestus."
                    ) ?? "Ettevõtte andmete kontrollimine ebaõnnestus."
                )
                return
            }
            this._confirmCompanyRegistration(response.result)
        }
        catch (error) {
            console.error("Company registration preview failed:", error)
            this.requestView.showError("Ettevõtte andmete kontrollimine ebaõnnestus.")
        }
        finally {
            this.requestView.loading = NO
        }
    }


    // The confirmation dialog is separate from the route-backed details dialog.
    _confirmCompanyRegistration(preview: {
        businessRegistryCode: string
        companyName: string
        officialEmail: string
        deliveryOutcome: RHEmailDeliveryOutcome
    }) {
        const shower = CBDialogViewShower.showQuestionDialog(
            CompanyMembershipRequestViewController._companyRegistrationConfirmationText(preview)
        )
        shower.yesButtonWasPressed = () => {
            shower.dialogView.dismiss()
            UIRoute.currentRoute
                .routeByRemovingComponentNamed(
                    CompanyMembershipRequestViewController.routeComponentName
                )
                .apply()
            this._performCompanyRegistration(preview.businessRegistryCode).then(undefined)
        }
    }


    // The confirmation describes where the email will really end up. Outside
    // production, messages to anyone except the acting user go to the test sink.
    static _companyRegistrationConfirmationText(
        preview: { companyName: string, deliveryOutcome: RHEmailDeliveryOutcome }
    ): string {
        const intro = `Kinnituskiri ettevõtte „${preview.companyName}“ registreerimiseks`
        const outcome = preview.deliveryOutcome
        if (outcome.kind === "sent") {
            return `${intro} saadetakse ettevõtte ametlikule e-posti aadressile ` +
                `„${outcome.recipient}“ (registriandmete põhjal), mitte sinu enda aadressile. ` +
                "Kas jätkata?"
        }
        return `${intro} saadetakse testkeskkonna e-posti aadressile „${outcome.recipient}“, ` +
            `mitte ettevõtte ametlikule aadressile „${outcome.originalRecipient}“ ` +
            "(algne saaja lisatakse teemareale). Kas jätkata?"
    }


    async _performCompanyRegistration(businessRegistryCode: string) {
        try {
            const response = await SocketClient.RegisterCompany({ businessRegistryCode })
            if (response.result?.success) {
                // Where the email actually went was already stated in the confirmation
                // question the user just answered - no need to repeat it here.
                CBDialogViewShower.alert(
                    "Kinnituskiri on saadetud. Ettevõtte lisamiseks ava kinnituskirjas olev " +
                    "link. Pärast kinnitamist oled ettevõtte peakasutaja."
                )
            }
            else {
                CBDialogViewShower.alert(
                    RHCore.sharedInstance.errorMessageFromResponse(
                        response,
                        "Ettevõtte registreerimistaotluse saatmine ebaõnnestus."
                    ) ?? "Ettevõtte registreerimistaotluse saatmine ebaõnnestus."
                )
            }
        }
        catch (error) {
            console.error("Company registration request failed:", error)
            CBDialogViewShower.alert("Ettevõtte registreerimistaotluse saatmine ebaõnnestus.")
        }
    }

}
